import { useState } from "react";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { Button, Tooltip } from "@mui/material";
import { useGetQuoteProducts } from "../../api/useProducts";
import { IQuoteProduct } from "../../shared/types";
import { EditorSection } from "../QuoteSectionBar/sectionUtils";
import CopyTermDialog from "./CopyTermDialog";
import { TermCopyPlan, termsInSection } from "./copyTermUtils";

type Props = {
  sections: EditorSection[];
  activeIndex: number;
  // The customer's catalog for a customer quote; undefined for a prospect quote, which uses the
  // general catalog exactly as QuoteLineItemGrid does (same react-query cache).
  productRows?: IQuoteProduct[];
  productsLoading?: boolean;
  productsError?: boolean;
  onCopy: (plan: TermCopyPlan) => void;
};

// Issue 52: the "Copy Term" toolbar button and its dialog. Disabled, with the reason, until the
// active section has a complete line and the catalog that decides availability is loaded - never
// a silent "nothing is offered" preview from a catalog that simply hasn't arrived.
const CopyTermButton = ({ sections, activeIndex, productRows, productsLoading = false, productsError = false, onCopy }: Props) => {
  const [open, setOpen] = useState(false);
  const products = useGetQuoteProducts({ enabled: productRows === undefined });
  const rows = productRows === undefined ? (products.data || []) : productRows;
  const loading = productRows === undefined ? products.isLoading || products.isFetching : productsLoading;
  const failed = productRows === undefined ? products.isError : productsError;
  const hasLines = termsInSection(sections[activeIndex]).length > 0;
  const reason = !hasLines ? "Add a complete line item first, then copy its term."
    : failed ? "The product catalog could not be loaded, so availability in other terms can't be checked."
    : loading ? "Loading the product catalog..."
    : "Copy this section's lines for one term into another term, to offer a term comparison.";
  const disabled = !hasLines || failed || loading;

  return <>
    <Tooltip enterDelay={disabled ? 0 : 1800} title={reason}><span>
      <Button variant="outlined" startIcon={<CompareArrowsIcon />} disabled={disabled} onClick={() => setOpen(true)}>Copy Term</Button>
    </span></Tooltip>
    <CopyTermDialog open={open} sections={sections} activeIndex={activeIndex} productRows={rows}
      onClose={() => setOpen(false)} onCopy={plan => { setOpen(false); onCopy(plan); }} />
  </>;
};

export default CopyTermButton;
